import React, { useState, useEffect, useCallback } from 'react';
import {
  Gift, ExternalLink, Copy, Clock, Tag, CheckCircle, XCircle, Sparkles, AlertCircle
} from 'lucide-react';
import { useCustomerAuth } from './contexts/CustomerAuthContext';
import { getStorefrontUrl } from './config/platformConfig';
import './styles/customer-promotions.css';

const VIEW_TABS = [
  { value: 'all',           label: 'All',     Icon: null },
  { value: 'ai-picks',      label: 'AI picks', Icon: Sparkles },
  { value: 'discounts',     label: 'Codes',   Icon: Tag },
  { value: 'notifications', label: 'Promos',  Icon: Gift }
];

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-NG', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const formatValue = (discount) => {
  if (!discount) return '';
  if (discount.type === 'PERCENTAGE') return `${discount.value}% OFF`;
  return `₦${discount.value} OFF`;
};

const isPromoExpired = (validUntil) => {
  if (!validUntil) return false;
  return new Date(validUntil) < new Date();
};

const CustomerPromotions = () => {
  const { customer } = useCustomerAuth();
  const [promotions, setPromotions] = useState([]);
  const [merchantDiscounts, setMerchantDiscounts] = useState([]);
  const [personalizedSuggestions, setPersonalizedSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewType, setViewType] = useState('all');

  const fetchPromotions = useCallback(async () => {
    setPromotions([]);
  }, []);

  const fetchMerchantDiscounts = useCallback(async () => {
    if (!customer?.id) return;
    try {
      const response = await fetch(`/api/customer/discounts/my-merchants?customerId=${customer.id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMerchantDiscounts(data.discounts || []);
      }
    } catch (err) {
      /* non-blocking */
    }
  }, [customer?.id]);

  const fetchPersonalizedSuggestions = useCallback(async () => {
    if (!customer?.id) return;
    try {
      const response = await fetch('/api/customer/discounts/personalized', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ customerId: customer.id })
      });
      if (response.ok) {
        const data = await response.json();
        setPersonalizedSuggestions(data.suggestions || []);
      }
    } catch (err) {
      /* non-blocking */
    }
  }, [customer?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchPromotions(), fetchMerchantDiscounts(), fetchPersonalizedSuggestions()]);
      } catch (err) {
        if (!cancelled) setError('Failed to load promotions and discounts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchPromotions, fetchMerchantDiscounts, fetchPersonalizedSuggestions]);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`/api/customer/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      setPromotions(prev =>
        prev.map(promo => (promo.id === notificationId ? { ...promo, isRead: true } : promo))
      );
    } catch (err) {
      /* non-blocking */
    }
  };

  const copyCode = (code, label = 'Code') => {
    navigator.clipboard.writeText(code);
    setSuccess(`${label} ${code} copied`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const visitStore = (promo) => {
    markAsRead(promo.id);
    const storeSlug = promo.metadata?.storeSlug || promo.store?.slug;
    if (storeSlug) {
      const storeUrl = getStorefrontUrl(storeSlug);
      if (storeUrl) window.open(storeUrl, '_blank');
    }
  };

  const visitStoreWithDiscount = async (discount) => {
    try {
      await fetch('/api/customer/discounts/track-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customerId:   customer?.id,
          discountId:   discount.id,
          discountCode: discount.code
        })
      });
    } catch (err) {
      /* non-blocking */
    }

    const getTransferToken = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
        const response = await fetch(`${backendUrl}/api/customer/auth/transfer-token`, {
          method: 'GET',
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          return { token: data.token, customerId: data.customerId };
        }
        return null;
      } catch (e) {
        return null;
      }
    };

    const storeSlug = discount.merchant?.slug;
    if (storeSlug) {
      const authData = await getTransferToken();
      const discountParam = discount.code ? `discount=${encodeURIComponent(discount.code)}` : '';
      const authParam     = authData ? `auth=${encodeURIComponent(authData.token)}&customerId=${authData.customerId}` : '';
      const params = [discountParam, authParam].filter(Boolean).join('&');
      const baseUrl = getStorefrontUrl(storeSlug);
      if (baseUrl) {
        const url = `${baseUrl}${params ? `?${params}` : ''}`;
        window.open(url, '_blank');
      }
    }
  };

  const displayItems = (() => {
    const items = [];
    if (viewType === 'all' || viewType === 'ai-picks') {
      items.push(...personalizedSuggestions
        .filter(s => !s.discount.endsAt || new Date(s.discount.endsAt) >= new Date())
        .map(s => ({ type: 'ai-suggestion', data: s })));
    }
    if (viewType === 'all' || viewType === 'discounts') {
      items.push(...merchantDiscounts
        .filter(d => !d.endsAt || new Date(d.endsAt) >= new Date())
        .map(d => ({ type: 'merchant-discount', data: d })));
    }
    if (viewType === 'all' || viewType === 'notifications') {
      items.push(...promotions
        .filter(p => !isPromoExpired(p.metadata?.validUntil))
        .map(p => ({ type: 'promo-notification', data: p })));
    }
    return items;
  })();

  if (loading) {
    return (
      <div className="cpromo">
        <div className="cpromo__skel cpromo__skel--head" />
        <div className="cpromo__grid">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="cpromo__skel cpromo__skel--card" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="cpromo">
      <header className="cpromo__head">
        <h1 className="cpromo__title">Offers & discounts</h1>
        <p className="cpromo__sub">
          {personalizedSuggestions.length} AI picks · {merchantDiscounts.length} codes · {promotions.length} promos
        </p>
      </header>

      <nav className="cpromo__tabs" aria-label="Filter offers">
        {VIEW_TABS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => setViewType(value)}
            className={`cpromo-tab ${viewType === value ? 'is-active' : ''}`}
            aria-pressed={viewType === value}
          >
            {Icon && <Icon size={12} aria-hidden />}
            {label}
          </button>
        ))}
      </nav>

      {error && (
        <div className="cpromo__alert cpromo__alert--error" role="alert">
          <XCircle size={14} aria-hidden /> <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="cpromo__alert cpromo__alert--success" role="status">
          <CheckCircle size={14} aria-hidden /> <span>{success}</span>
        </div>
      )}

      {displayItems.length === 0 ? (
        <div className="cpromo__empty">
          <Gift size={32} aria-hidden />
          <div>
            <div className="cpromo__empty-title">No offers right now</div>
            <div className="cpromo__empty-sub">
              {customer?.id
                ? 'Shop a store to start unlocking exclusive offers.'
                : 'Sign in to see personalised offers from stores you love.'}
            </div>
          </div>
        </div>
      ) : (
        <ul className="cpromo__grid">
          {displayItems.map((item, index) => {
            if (item.type === 'ai-suggestion') {
              const suggestion = item.data;
              const discount = suggestion.discount;
              const expired = discount.endsAt && new Date(discount.endsAt) < new Date();
              const storeName = discount.merchant?.name || 'Store';
              return (
                <li
                  key={`ai-${discount.id || index}`}
                  className={`cpromo-card ${expired ? 'is-expired' : ''} cpromo-card--ai`}
                >
                  <header className="cpromo-card__head">
                    <div className="cpromo-card__store">
                      <span className="cpromo-card__avatar">{storeName.charAt(0).toUpperCase()}</span>
                      <div className="cpromo-card__store-info">
                        <div className="cpromo-card__store-name">{storeName}</div>
                        <div className="cpromo-card__store-meta">
                          <Sparkles size={11} aria-hidden /> AI pick · {suggestion.score}/100
                        </div>
                      </div>
                    </div>
                    {expired
                      ? <span className="cpromo-badge cpromo-badge--expired">Expired</span>
                      : <span className="cpromo-badge cpromo-badge--ai">{(suggestion.priority || '').toLowerCase()}</span>}
                  </header>

                  <div className="cpromo-card__body">
                    <h2 className="cpromo-card__name">{discount.name}</h2>
                    <div className="cpromo-card__value">{formatValue(discount)}</div>

                    {suggestion.reasons && suggestion.reasons.length > 0 && (
                      <ul className="cpromo-card__reasons">
                        {suggestion.reasons.slice(0, 2).map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    )}

                    <div className="cpromo-card__code">
                      <div className="cpromo-card__code-label">Discount code</div>
                      <div className="cpromo-card__code-row">
                        <code className="cpromo-card__code-value">{discount.code}</code>
                        <button
                          type="button"
                          onClick={() => copyCode(discount.code, 'Discount')}
                          disabled={expired}
                          className="cpromo-card__copy"
                        >
                          <Copy size={12} aria-hidden /> Copy
                        </button>
                      </div>
                    </div>

                    {suggestion.suggestedMessage && (
                      <p className="cpromo-card__msg">“{suggestion.suggestedMessage}”</p>
                    )}

                    <button
                      type="button"
                      onClick={() => visitStoreWithDiscount(discount)}
                      disabled={expired}
                      className="cpromo-card__cta"
                    >
                      {expired ? 'Offer expired' : <>Shop now <ExternalLink size={14} aria-hidden /></>}
                    </button>
                  </div>
                </li>
              );
            }

            if (item.type === 'merchant-discount') {
              const discount = item.data;
              const expired = discount.endsAt && new Date(discount.endsAt) < new Date();
              const storeName = discount.merchant?.name || 'Store';
              const storeLogo = discount.merchant?.logo;
              return (
                <li
                  key={`discount-${discount.id || index}`}
                  className={`cpromo-card ${expired ? 'is-expired' : ''}`}
                >
                  <header className="cpromo-card__head">
                    <div className="cpromo-card__store">
                      {storeLogo
                        ? <img src={storeLogo} alt={storeName} className="cpromo-card__logo" onError={(e) => { e.target.style.display = 'none'; }} />
                        : <span className="cpromo-card__avatar">{storeName.charAt(0).toUpperCase()}</span>}
                      <div className="cpromo-card__store-info">
                        <div className="cpromo-card__store-name">{storeName}</div>
                        {discount.urgencyMessage && (
                          <div className="cpromo-card__store-meta cpromo-card__store-meta--warn">
                            <AlertCircle size={11} aria-hidden /> {discount.urgencyMessage}
                          </div>
                        )}
                      </div>
                    </div>
                    {expired
                      ? <span className="cpromo-badge cpromo-badge--expired">Expired</span>
                      : (discount.alreadyUsed && <span className="cpromo-badge cpromo-badge--used">Used</span>)}
                  </header>

                  <div className="cpromo-card__body">
                    <h2 className="cpromo-card__name">{discount.name}</h2>
                    <div className="cpromo-card__value">
                      {formatValue(discount)}
                      {discount.minOrderAmount && (
                        <span className="cpromo-card__value-sub"> on orders over ₦{discount.minOrderAmount}</span>
                      )}
                    </div>
                    {discount.description && (
                      <p className="cpromo-card__desc">{discount.description}</p>
                    )}
                    <div className="cpromo-card__code">
                      <div className="cpromo-card__code-label">Discount code</div>
                      <div className="cpromo-card__code-row">
                        <code className="cpromo-card__code-value">{discount.code}</code>
                        <button
                          type="button"
                          onClick={() => copyCode(discount.code, 'Discount')}
                          disabled={expired}
                          className="cpromo-card__copy"
                        >
                          <Copy size={12} aria-hidden /> Copy
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => visitStoreWithDiscount(discount)}
                      disabled={expired}
                      className="cpromo-card__cta"
                    >
                      {expired ? 'Offer expired' : <>Shop & save <ExternalLink size={14} aria-hidden /></>}
                    </button>
                  </div>
                </li>
              );
            }

            // promo-notification
            const promo = item.data;
            const expired = isPromoExpired(promo.metadata?.validUntil);
            const storeName = promo.store?.name || 'Store';
            const storeLogo = promo.metadata?.storeLogo || promo.store?.logo;
            const promoCode = promo.metadata?.promoCode;
            const discountValue = promo.metadata?.discount;
            const validUntil = promo.metadata?.validUntil;
            return (
              <li
                key={promo.id}
                className={`cpromo-card ${expired ? 'is-expired' : ''} ${!promo.isRead ? 'is-unread' : ''}`}
              >
                <header className="cpromo-card__head">
                  <div className="cpromo-card__store">
                    {storeLogo
                      ? <img src={storeLogo} alt={storeName} className="cpromo-card__logo" onError={(e) => { e.target.style.display = 'none'; }} />
                      : <span className="cpromo-card__avatar">{storeName.charAt(0).toUpperCase()}</span>}
                    <div className="cpromo-card__store-info">
                      <div className="cpromo-card__store-name">{storeName}</div>
                      <div className="cpromo-card__store-meta">
                        <Clock size={11} aria-hidden /> {formatDate(promo.createdAt)}
                      </div>
                    </div>
                  </div>
                  {expired
                    ? <span className="cpromo-badge cpromo-badge--expired">Expired</span>
                    : (!promo.isRead && <span className="cpromo-badge cpromo-badge--new">New</span>)}
                </header>

                <div className="cpromo-card__body">
                  <h2 className="cpromo-card__name">{promo.title}</h2>
                  {discountValue && <div className="cpromo-card__value">{discountValue}</div>}
                  {promo.message && <p className="cpromo-card__desc">{promo.message}</p>}

                  {promoCode && (
                    <div className="cpromo-card__code">
                      <div className="cpromo-card__code-label">Promo code</div>
                      <div className="cpromo-card__code-row">
                        <code className="cpromo-card__code-value">{promoCode}</code>
                        <button
                          type="button"
                          onClick={() => copyCode(promoCode, 'Promo')}
                          disabled={expired}
                          className="cpromo-card__copy"
                        >
                          <Copy size={12} aria-hidden /> Copy
                        </button>
                      </div>
                    </div>
                  )}

                  {validUntil && (
                    <div className={`cpromo-card__valid ${expired ? 'is-expired' : ''}`}>
                      <Clock size={12} aria-hidden />
                      {expired ? 'Expired on' : 'Valid until'} {formatDate(validUntil)}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => visitStore(promo)}
                    disabled={expired}
                    className="cpromo-card__cta"
                  >
                    {expired ? 'Offer expired' : <>Shop now <ExternalLink size={14} aria-hidden /></>}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CustomerPromotions;
